/**
 * Storage Cleanup Utility
 * Centralized management for clearing browser storage on logout
 *
 * SECURITY: Ensures all sensitive data is properly cleared
 */

import { createLogger } from '@xanectech/lib';
import { deleteKeys } from '@xanectech/encryption';

const logger = createLogger('Auth - storageCleanup');

export interface StorageCleanupOptions {
  /**
   * Clear all localStorage (not just sensitive keys)
   */
  clearAllLocalStorage?: boolean;

  /**
   * Clear encryption keys from IndexedDB
   * Default: true
   */
  clearEncryptionKeys?: boolean;

  /**
   * User ID for clearing user-specific data
   */
  userId?: string;
}

/**
 * List of sensitive localStorage keys that should always be cleared
 */
const SENSITIVE_KEYS = [
  'user_roles',
  'current_role',
  'field_data',
  'sensitive_data',
  'encryption_key',
  'auth_token',
  'user_encryption_password', // Legacy - should never exist
  'temp_login_password', // Temporary password storage
  'offlineMockUser', // Offline mode data
];

/**
 * Clear all sessionStorage
 * SECURITY: sessionStorage can contain sensitive data
 */
export function clearSessionStorage(): void {
  try {
    sessionStorage.clear();
    logger.info('Cleared sessionStorage', {}, 'auth');
  } catch (error) {
    logger.error(
      'Failed to clear sessionStorage',
      error instanceof Error ? error : { error: String(error) },
      'auth',
    );
  }
}

/**
 * Clear sensitive localStorage keys
 */
export function clearSensitiveLocalStorage(): void {
  try {
    SENSITIVE_KEYS.forEach((key) => {
      localStorage.removeItem(key);

      // Also check for dynamic keys with patterns
      Object.keys(localStorage).forEach((storeKey) => {
        if (storeKey.includes(key)) {
          localStorage.removeItem(storeKey);
        }
      });
    });
    logger.info('✅ Cleared sensitive localStorage keys');
  } catch (error) {
    logger.error(
      'Failed to clear sensitive localStorage:',
      error instanceof Error ? error : { error: String(error) },
      'auth',
    );
  }
}

/**
 * Clear all localStorage
 */
export async function clearAllLocalStorage(): Promise<void> {
  try {
    localStorage.clear();
    logger.info('✅ Cleared all localStorage', {}, 'auth');
  } catch (error) {
    logger.error(
      'Failed to clear all localStorage',
      error instanceof Error ? error : { error: String(error) },
      'auth',
    );
  }
}

/**
 * Clear encryption keys from IndexedDB
 */
export async function clearEncryptionKeys(userId: string): Promise<void> {
  try {
    await deleteKeys(userId);
    logger.info('✅ Cleared encryption keys from IndexedDB', {}, 'auth');
  } catch (error) {
    logger.error(
      'Failed to clear encryption keys from IndexedDB',
      error instanceof Error ? error : { error: String(error) },
      'auth',
    );
  }
}

/**
 * Comprehensive storage cleanup on logout
 *
 * @param options - Cleanup options
 */
export async function performLogoutCleanup(
  options: StorageCleanupOptions = {},
): Promise<void> {
  const {
    clearAllLocalStorage: shouldClearAll = false,
    clearEncryptionKeys: shouldClearKeys = true,
    userId,
  } = options;

  logger.info('🧹 Starting logout cleanup...', {});

  // CRITICAL: Always clear sessionStorage (contains temp passwords)
  clearSessionStorage();

  // Clear localStorage
  if (shouldClearAll) {
    await clearAllLocalStorage();
  } else {
    clearSensitiveLocalStorage();
  }

  // Clear encryption keys from IndexedDB
  if (shouldClearKeys && userId) {
    await clearEncryptionKeys(userId);
  }

  logger.info('✅ Logout cleanup complete', {}, 'auth');
}

/**
 * Emergency cleanup - clears EVERYTHING
 * Use when user wants to completely reset their local data
 */
export async function performEmergencyCleanup(userId?: string): Promise<void> {
  logger.warn(
    '⚠️ Performing emergency cleanup - all data will be lost',
    {},
    'auth',
  );

  // Clear all browser storage
  clearSessionStorage();
  await clearAllLocalStorage();

  // Clear IndexedDB
  if (userId) {
    await clearEncryptionKeys(userId);
  }

  // Clear any other IndexedDB databases if needed
  try {
    const databases = await indexedDB.databases();
    for (const db of databases) {
      if (db.name) {
        indexedDB.deleteDatabase(db.name);
        logger.info(`🗑️ Deleted IndexedDB: ${db.name}`, {}, 'auth');
      }
    }
  } catch (error) {
    logger.error(
      'Failed to list/delete IndexedDB databases',
      error instanceof Error ? error : { error: String(error) },
      'auth',
    );
  }

  logger.info('✅ Emergency cleanup complete', {}, 'auth');
}
